import React from "react";
import { Draggable, Droppable } from "@hello-pangea/dnd";
import { ChevronDown, ChevronUp, GripVertical } from "lucide-react";
import {
  getReorderableFamilyNodes,
  isFamilyGroup,
  isFamilyListReorderable,
  makeGroupDroppableId,
  SOLE_FAMILY_REORDER_HINT,
} from "./categoryGroupReorder";
import "./SortableCategoryGroup.scss";

function StaticCategoryGroupSlot({ node, listIndex, children }) {
  return (
    <Draggable
      draggableId={`static:${node.slug}`}
      index={listIndex}
      isDragDisabled
    >
      {(provided) => (
        <div
          className="category-group-static-slot"
          ref={provided.innerRef}
          {...provided.draggableProps}
        >
          {children}
        </div>
      )}
    </Draggable>
  );
}

export function SortableCategoryGroupList({
  parentSlug,
  childNodes,
  renderChild,
  savedSlug,
  canReorder,
  isSavePending,
  onMove,
  listsRef,
}) {
  const droppableId = makeGroupDroppableId(parentSlug);
  const familyNodes = getReorderableFamilyNodes(childNodes);
  const canReorderFamilies = Boolean(canReorder && familyNodes.length);
  if (listsRef) {
    listsRef.current[droppableId] = canReorderFamilies
      ? {
          familySlugs: familyNodes.map((child) => child.slug),
          childNodes,
        }
      : { familySlugs: [], childNodes: [] };
  }

  if (!canReorderFamilies) {
    return childNodes.map((child) => renderChild(child, null));
  }

  const familyIndexBySlug = new Map(
    familyNodes.map((child, index) => [child.slug, index]),
  );

  return (
    <Droppable
      droppableId={droppableId}
      type={droppableId}
      renderClone={(provided, _snapshot, rubric) => {
        const child = childNodes[rubric.source.index];
        return (
          <div
            className="category-group-clone"
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            <div className="category-group-clone__chip" dir="rtl">
              <span className="category-group__handle" aria-hidden>
                <GripVertical size={18} strokeWidth={2} />
              </span>
              <span className="category-group__clone-title">
                {child?.name || ""}
              </span>
            </div>
          </div>
        );
      }}
    >
      {(provided) => (
        <div
          className="category-group-list"
          ref={provided.innerRef}
          {...provided.droppableProps}
        >
          {childNodes.map((child, listIndex) => {
            const familyIndex = familyIndexBySlug.get(child.slug);
            const isFamily = isFamilyGroup(child);

            if (!isFamily) {
              return (
                <StaticCategoryGroupSlot
                  key={child.id ?? child.slug ?? listIndex}
                  node={child}
                  listIndex={listIndex}
                >
                  {renderChild(child, null)}
                </StaticCategoryGroupSlot>
              );
            }

            return (
              <React.Fragment key={child.id ?? child.slug ?? listIndex}>
                {renderChild(child, {
                  listIndex,
                  familyIndex,
                  listLength: familyNodes.length,
                  isSavePending,
                  saved: savedSlug === child.slug,
                  onMoveUp: () =>
                    onMove(parentSlug, familyIndex, familyIndex - 1),
                  onMoveDown: () =>
                    onMove(parentSlug, familyIndex, familyIndex + 1),
                })}
              </React.Fragment>
            );
          })}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}

export default function SortableCategoryGroup({
  node,
  listIndex,
  familyIndex,
  listLength,
  isSavePending,
  saved,
  onMoveUp,
  onMoveDown,
  heading,
  children,
}) {
  const canMoveUp = familyIndex > 0;
  const canMoveDown = familyIndex < listLength - 1;
  const cannotReorder = !isFamilyListReorderable(listLength);
  const dragDisabled = Boolean(isSavePending || cannotReorder);
  const handleLabel = cannotReorder
    ? SOLE_FAMILY_REORDER_HINT
    : "גרור לשינוי סדר המקבץ";

  return (
    <Draggable
      draggableId={String(node.slug)}
      index={listIndex}
      isDragDisabled={dragDisabled}
    >
      {(provided, snapshot) => (
        <div
          className={`category-group${
            snapshot.isDragging ? " category-group--dragging" : ""
          }`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          style={{
            ...provided.draggableProps.style,
            ...(snapshot.isDragging
              ? {
                  height: 0,
                  minHeight: 0,
                  margin: 0,
                  padding: 0,
                  overflow: "hidden",
                }
              : null),
          }}
          data-category-id={node.slug}
          data-parent-category-id={node.parentSlug}
          data-family={node.isFamily}
          data-category-scroll-anchor={node.slug}
        >
          <div className="category-group__heading">
            <div className="category-group__controls">
              <span
                className={`category-group__handle${
                  dragDisabled ? " category-group__handle--disabled" : ""
                }`}
                {...provided.dragHandleProps}
                aria-label={handleLabel}
                aria-disabled={dragDisabled || undefined}
                title={handleLabel}
              >
                <GripVertical size={18} strokeWidth={2} />
              </span>
              <div className="category-group__arrows">
                <button
                  type="button"
                  className="category-group__move-btn"
                  onClick={onMoveUp}
                  onMouseDown={(event) => event.stopPropagation()}
                  disabled={!canMoveUp || dragDisabled}
                  aria-label="העבר מקבץ למעלה"
                >
                  <ChevronUp size={14} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="category-group__move-btn"
                  onClick={onMoveDown}
                  onMouseDown={(event) => event.stopPropagation()}
                  disabled={!canMoveDown || dragDisabled}
                  aria-label="העבר מקבץ למטה"
                >
                  <ChevronDown size={14} strokeWidth={2} />
                </button>
              </div>
            </div>
            <div className="category-group__heading-main">{heading}</div>
            {saved ? (
              <span className="category-group__saved" aria-live="polite">
                נשמר
              </span>
            ) : null}
          </div>
          <div className="category-group__body">{children}</div>
        </div>
      )}
    </Draggable>
  );
}
